/**
 * Pure state helpers for DeskeyPaint — history, vector fill, and the
 * geometry fixes for drag/resize. No Konva or DOM here so it unit-tests.
 */
export const MAX_HISTORY = 40;
const MIN_BOX_SIZE = 5;
const POLYGON_DUPLICATE_GAP = 3;

// Shapes Konva positions by their centre (Circle, Ellipse, RegularPolygon,
// Star). DeskeyPaint renders them with a negative offset so node.x()/y()
// is the top-left corner like every other shape.
const CENTER_ANCHORED = new Set(['ellipse', 'circle', 'triangle', 'diamond', 'star', 'hexagon', 'pentagon', 'octagon']);

const cloneSnapshot = (snap) => ({
  objects: JSON.parse(JSON.stringify(snap.objects || [])),
  rasterData: snap.rasterData || null,
});

/** @param {{objects: object[], rasterData: string|null}} snap */
export function initialHistory(snap) {
  return { stack: [cloneSnapshot(snap)], idx: 0 };
}

export function pushHistory(history, snap) {
  const stack = [...history.stack.slice(0, history.idx + 1), cloneSnapshot(snap)].slice(-MAX_HISTORY);
  return { stack, idx: stack.length - 1 };
}

/** Move the cursor by `delta`; returns the same object when it can't move. */
export function stepHistory(history, delta) {
  const idx = history.idx + delta;
  if (idx < 0 || idx >= history.stack.length) return history;
  return { ...history, idx };
}

export function isVectorFillable(obj) {
  return Boolean(obj) && (obj.type === 'shape' || obj.type === 'polygon');
}

export function withVectorFill(obj, color) {
  return { ...obj, fill: color, fillMode: obj.stroke ? 'both' : 'fill' };
}

export function isCenterAnchored(shapeType) {
  return CENTER_ANCHORED.has(shapeType);
}

/** New box after a Transformer resize: scale the stored size (not the node's). */
export function resizedBox(obj, { x, y, scaleX, scaleY, rotation }) {
  return {
    ...obj,
    x,
    y,
    width: Math.max(MIN_BOX_SIZE, Math.abs(obj.width || 0) * Math.abs(scaleX)),
    height: Math.max(MIN_BOX_SIZE, Math.abs(obj.height || 0) * Math.abs(scaleY)),
    rotation,
  };
}

/** Map a flat [x0,y0,x1,y1,…] array through `fn({x,y}) => {x,y}`. */
export function mapPoints(points, fn) {
  const out = [];
  for (let i = 0; i < points.length; i += 2) {
    const p = fn({ x: points[i], y: points[i + 1] });
    out.push(p.x, p.y);
  }
  return out;
}

/** Drop consecutive clicks that land on (almost) the same spot. */
export function dedupePolygonPoints(points, minGap = POLYGON_DUPLICATE_GAP) {
  return points.reduce((kept, p) => {
    const prev = kept[kept.length - 1];
    if (prev && Math.hypot(p.x - prev.x, p.y - prev.y) < minGap) return kept;
    return [...kept, p];
  }, []);
}

const FILL_TOLERANCE = 32;
const CHANNEL_MAX = 255;

function channelDistance(data, i, rgba) {
  return Math.max(
    Math.abs(data[i] - rgba[0]), Math.abs(data[i + 1] - rgba[1]),
    Math.abs(data[i + 2] - rgba[2]), Math.abs(data[i + 3] - rgba[3]),
  );
}

function touchesRegion(region, vi, x, y, w, h) {
  return (x > 0 && region[vi - 1]) || (x < w - 1 && region[vi + 1])
    || (y > 0 && region[vi - w]) || (y < h - 1 && region[vi + w]);
}

/**
 * Stack-based flood fill on an ImageData-like `{ data, width, height }`
 * (mutated in place). Canvas strokes are anti-aliased, so the one-pixel ring
 * just outside the region is blended toward the fill in proportion to how
 * close it is to the region's colour — otherwise every outline keeps a light,
 * dotted-looking halo.
 */
export function floodFillImageData(img, startX, startY, fill, tolerance = FILL_TOLERANCE) {
  const { data, width: w, height: h } = img;
  const si = (startY * w + startX) * 4;
  const target = [data[si], data[si + 1], data[si + 2], data[si + 3]];
  if (channelDistance(data, si, fill) === 0) return;

  const region = new Uint8Array(w * h);
  const stack = [startX, startY];
  while (stack.length) {
    const y = stack.pop();
    const x = stack.pop();
    if (x < 0 || x >= w || y < 0 || y >= h) continue;
    const vi = y * w + x;
    if (region[vi] || channelDistance(data, vi * 4, target) > tolerance) continue;
    region[vi] = 1;
    stack.push(x + 1, y, x - 1, y, x, y + 1, x, y - 1);
  }

  for (let vi = 0; vi < w * h; vi++) {
    if (region[vi]) data.set(fill, vi * 4);
  }

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const vi = y * w + x;
      if (region[vi] || !touchesRegion(region, vi, x, y, w, h)) continue;
      const i = vi * 4;
      const keep = Math.min(1, channelDistance(data, i, target) / CHANNEL_MAX);
      for (let c = 0; c < 4; c++) data[i + c] = Math.round(data[i + c] * keep + fill[c] * (1 - keep));
    }
  }
}

export const ZOOMS = [0.5, 1, 2, 4, 8];

/** Next zoom level up (delta 1, magnifier) or down (delta -1, minifier). */
export function zoomStep(zoom, delta) {
  const nearest = ZOOMS.reduce((best, z, i) => (Math.abs(z - zoom) < Math.abs(ZOOMS[best] - zoom) ? i : best), 0);
  const idx = Math.min(ZOOMS.length - 1, Math.max(0, nearest + delta));
  return ZOOMS[idx];
}

/** Anything on the canvas that closing Paint would throw away. */
export function hasDrawing({ objects, raster, floating }) {
  return objects.length > 0 || Boolean(raster) || Boolean(floating);
}
